import { useEffect, useState } from 'react';
import {
  getThemePref,
  resolveTheme,
  setThemePref,
  onThemeChange,
  type ThemePref,
} from '@/shared/theme';

function useThemePref(): ThemePref {
  const [pref, setPref] = useState<ThemePref>(() => getThemePref());
  useEffect(() => onThemeChange(setPref), []);
  return pref;
}

// Quick header toggle: flips light↔dark (an explicit choice that persists).
export function ThemeToggle({ className = '' }: { className?: string }) {
  const pref = useThemePref();
  const dark = resolveTheme(pref) === 'dark';
  return (
    <button
      type="button"
      onClick={() => setThemePref(dark ? 'light' : 'dark')}
      className={`text-slate-500 transition hover:text-slate-800 ${className}`}
      title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
      aria-label="Toggle dark mode"
    >
      {dark ? <SunIcon /> : <MoonIcon />}
    </button>
  );
}

// Explicit 3-way control for Settings: System / Light / Dark.
export function ThemeControl() {
  const pref = useThemePref();
  const opts: ThemePref[] = ['system', 'light', 'dark'];
  return (
    <div className="inline-flex overflow-hidden rounded border border-slate-300 text-xs">
      {opts.map((o, i) => (
        <button
          key={o}
          type="button"
          onClick={() => setThemePref(o)}
          className={`px-3 py-1 capitalize ${i > 0 ? 'border-l border-slate-300' : ''} ${
            pref === o ? 'bg-blue-600 text-white' : 'bg-card text-slate-700 hover:bg-slate-100'
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

function SunIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </svg>
  );
}
